import {EditorState, StateEffect, StateField, Transaction} from '@codemirror/state'
import {EditorView} from '@codemirror/view'

/** Effect that will be triggered when focus changes.*/
const focusChangeEffect = StateEffect.define<boolean>()

/**
 * Confusingly, `EditorView.focusChangeEffect` is a `Facet`, not a `StateEffect` itself - we still have to
 * register our own effect to be able to 'listen' to the event.
 */
const focusObserver = EditorView.focusChangeEffect.of((_, focusing) => {
  return focusChangeEffect.of(focusing)
})

/** Field to store the focus state on the editor state. */
const isFocusedField = StateField.define<boolean>({
  create: () => false,
  update: (prev, tr) => tr.effects.find(eff => eff.is(focusChangeEffect))?.value ?? prev,
  provide: () => [focusObserver],
})

/**
 * While the focus state of the editor can be determined from `view.hasFocus`, a `StateField` plugin would not be
 * provided the `EditorView` (because state is decoupled from UI). So this plugin mirrors the editor's UI focus state
 * onto the editor state, and triggers an update effect when that changes. This is useful for other plugins that need
 * to know whether the editor is focused or not.
 */
export const focusedExtension = [isFocusedField]

/** Get the focus state from the editor state. Throws an error if `focusedExtension` is not installed. */
export function isFocused(state: EditorState) {
  return state.field(isFocusedField)
}

/** Check if the focus state changed in this transaction. */
export function focusChanged(transaction: Transaction) {
  return transaction.effects.some(eff => eff.is(focusChangeEffect))
}
